import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  Divider,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  getCurrentEnvironment,
  ENVIRONMENTS,
  type Environment,
} from '@/config/environment';

export const EnvironmentSwitcher: React.FC = () => {
  const [currentEnv] = useState<Environment>(() => {
    const env = getCurrentEnvironment();
    return env;
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const config = ENVIRONMENTS[currentEnv];

  return (
    <>
      <Tooltip title={`Current Environment: ${config.displayName}`}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: 'text.secondary',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <CloudIcon />
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 8,
                height: 8,
                backgroundColor: config.badgeColor,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            />
          </Box>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Current Environment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Auto-detected from URL
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckIcon fontSize="small" sx={{ color: config.badgeColor }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {config.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {config.apiBaseUrl.replace('https://', '').replace('http://', '')}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: config.badgeColor,
                borderRadius: '50%',
              }}
            />
          </Box>
        </Box>

        <Divider />
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            💡 To switch environments:
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            • Production: admin.polarride.com
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            • Staging: admin-staging.polarride.com
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            • Dev: localhost:3000
          </Typography>
        </Box>
      </Menu>
    </>
  );
};
